import React, { ReactNode, useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Home, 
  BookOpen, 
  MessageSquare, 
  User, 
  BarChart3, 
  Users, 
  CreditCard, 
  Settings, 
  LogOut,
  Menu,
  X,
  Layout as LayoutIcon,
  Globe,
  Bell, // Import Bell icon for notifications
  Download // Import Download icon
} from 'lucide-react';
import { api, API_BASE_URL } from '../api';

interface LayoutProps {
  children: ReactNode;
}

interface NavItem {
  name: string;
  href: string;
  icon: string;
  showBadge?: boolean;
  isDownload?: boolean;
  downloadName?: string;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);

  const fetchUnreadMessagesCount = useCallback(async () => {
    if (!user || !localStorage.getItem('token')) return;
    try {
      const response = await api.get<{ unreadCount: number }>('/messages/unread/count');
      setUnreadMessageCount(response.data.unreadCount);
    } catch (error) {
      console.error('Error fetching unread message count:', error);
    }
  }, [user]);

  useEffect(() => {
    fetchUnreadMessagesCount();

    const pollingInterval = setInterval(() => {
      fetchUnreadMessagesCount();
    }, 300000); // Poll every 5 minutes

    return () => clearInterval(pollingInterval);
  }, [fetchUnreadMessagesCount]);

  const downloadLinks = {
    android: `${API_BASE_URL}/api/downloads/android`,
    ios: `${API_BASE_URL}/api/downloads/ios`,
  };

  const getNavigationItems = (): NavItem[] => {
    if (!user?.role) return [];

    const baseItems = [
      { name: 'Dashboard', href: `/${user.role}/dashboard`, icon: 'Home' },
    ];

    if (user.role === 'student') {
      return [
        ...baseItems,
        { name: 'Mes Cours', href: '/student/courses', icon: 'BookOpen' },
        { name: 'Quiz & Tests', href: '/student/quizzes', icon: 'BarChart3' },
        { name: 'Statistiques', href: '/student/stats', icon: 'BarChart3' },
        { name: 'Messages', href: '/student/messages', icon: 'MessageSquare', showBadge: true },
        { name: 'Profil', href: '/student/profile', icon: 'User' },
        { name: 'App Android', href: downloadLinks.android, icon: 'Download', isDownload: true, downloadName: 'formacademy-android.apk' },
        { name: 'App iOS', href: downloadLinks.ios, icon: 'Download', isDownload: true, downloadName: 'formacademy-ios-app.ipa' },
      ];
    } else if (user.role === 'professor') {
      return [
        ...baseItems,
        { name: 'Mes Cours', href: '/professor/courses', icon: 'BookOpen' },
        { name: 'Quiz & Tests', href: '/professor/quizzes', icon: 'BarChart3' },
        { name: 'Messages', href: '/professor/messages', icon: 'MessageSquare', showBadge: true },
        { name: 'Profil', href: '/professor/profile', icon: 'User' },
        { name: 'App Android', href: downloadLinks.android, icon: 'Download', isDownload: true, downloadName: 'formacademy-android.apk' },
        { name: 'App iOS', href: downloadLinks.ios, icon: 'Download', isDownload: true, downloadName: 'formacademy-ios-app.ipa' },
      ];
    } else if (user.role === 'admin') {
      return [
        ...baseItems,
        { name: 'Utilisateurs', href: '/admin/users', icon: 'Users' },
        { name: 'Categorie', href: '/admin/category', icon: 'BookOpen' },
        { name: 'Cours', href: '/admin/courses', icon: 'BookOpen' },
        { name: 'Quiz & Tests', href: '/admin/quizzes', icon: 'BarChart3' },
        { name: 'Paiements', href: '/admin/payments', icon: 'CreditCard' },
        { name: 'Statistiques', href: '/admin/stats', icon: 'BarChart3' },
        { name: 'Messages', href: '/admin/messages', icon: 'MessageSquare', showBadge: true },
        { name: 'Contenu du site', href: '/admin/site-content', icon: 'LayoutIcon' },
        { name: 'Paramètres du site', href: '/admin/site-settings', icon: 'Globe' },
        { name: 'App Android', href: downloadLinks.android, icon: 'Download', isDownload: true, downloadName: 'formacademy-android.apk' },
        { name: 'App iOS', href: downloadLinks.ios, icon: 'Download', isDownload: true, downloadName: 'formacademy-ios-app.ipa' },
      ];
    }
    return baseItems;
  };

  const getIconComponent = (iconName: string) => {
    switch (iconName) {
      case 'Home': return Home;
      case 'BookOpen': return BookOpen;
      case 'MessageSquare': return MessageSquare;
      case 'User': return User;
      case 'BarChart3': return BarChart3;
      case 'Users': return Users;
      case 'CreditCard': return CreditCard;
      case 'Settings': return Settings;
      case 'LayoutIcon': return LayoutIcon;
      case 'Globe': return Globe;
      case 'Download': return Download;
      default: return Home;
    }
  };

  const navigationItems = getNavigationItems();

  const getPageTitle = () => {
    const path = location.pathname;
    const segment = path.split('/').filter(Boolean);
    const roleLabel = user?.role ? (user.role.charAt(0).toUpperCase() + user.role.slice(1)) : '';
    if (segment.length === 0) return roleLabel;
    if (segment.length === 1) return segment[0].charAt(0).toUpperCase() + segment[0].slice(1);

    const sectionMap: Record<string, string> = {
      dashboard: 'Tableau de bord',
      courses: 'Mes Cours',
      quizzes: 'Quiz & Tests',
      messages: 'Messages',
      profile: 'Profil',
      stats: 'Statistiques',
      users: 'Utilisateurs',
      category: 'Catégories',
      payments: 'Paiements',
      'site-content': 'Contenu du site',
      'site-settings': 'Paramètres du site',
    };

    const key = segment[1];
    if (sectionMap[key]) return sectionMap[key];
    return key.charAt(0).toUpperCase() + key.slice(1);
  };

  const currentPageTitle = getPageTitle();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div 
        className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="flex items-center justify-between h-16 px-4 bg-black text-[#FFB639]">
          <div className="flex items-center">
            <div className="flex-shrink-0 w-8 h-8 rounded-full overflow-hidden">
              <img 
                src="/logo.png" 
                alt="Logo" 
                className="w-full h-full object-cover"
              />
            </div>
            <span className="ml-2 font-bold text-lg">FORMACADEMY</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-[#FFB639]"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <nav className="mt-5 px-2">
          <div className="space-y-1">
            {navigationItems.map((item) => {
              const Icon = getIconComponent(item.icon);
              const isDownloadLink = Boolean(item.isDownload);
              const isActive = !isDownloadLink && location.pathname === item.href;
              const sharedClasses = `
                group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-150
                ${isActive 
                  ? 'bg-[#FFB639]/10 text-black' 
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }
              `;

              const content = (
                <>
                  <Icon 
                    className={`
                      mr-3 h-5 w-5 transition-colors duration-150
                      ${isActive ? 'text-[#FFB639]' : 'text-gray-400 group-hover:text-gray-500'}
                    `}
                  />
                  {item.name}
                  {item.showBadge && unreadMessageCount > 0 && (
                    <span className="ml-auto px-2 py-0.5 text-xs font-semibold bg-red-500 text-white rounded-full">
                      {unreadMessageCount}
                    </span>
                  )}
                </>
              );

              if (isDownloadLink) {
                return (
                  <a
                    key={item.name}
                    href={item.href}
                    className={sharedClasses}
                    onClick={() => setSidebarOpen(false)}
                    download={item.downloadName}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {content}
                  </a>
                );
              }

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={sharedClasses}
                  onClick={() => setSidebarOpen(false)}
                >
                  {content}
                </Link>
              );
            })}
          </div>
        </nav>

        <div className="absolute bottom-0 w-full p-4">
          <button
            onClick={handleLogout}
            className="flex items-center w-full px-2 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50 hover:text-gray-900 transition-colors duration-150"
          >
            <LogOut className="mr-3 h-5 w-5 text-gray-400" />
            Déconnexion
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4 gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-gray-600 hover:text-gray-900"
              >
                <Menu className="h-6 w-6" />
              </button>
              <div>
                <h1 className="text-lg font-semibold text-gray-900 leading-tight">
                  {currentPageTitle}
                </h1>
                <p className="text-xs text-gray-500 hidden sm:block">
                  Bonjour, <span className="font-medium text-gray-700">{user?.name}</span> — Bienvenue sur votre espace
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {unreadMessageCount > 0 && (
                <button
                  onClick={() => navigate(`/${user?.role}/messages`)}
                  className="relative p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                  title="Messages non lus"
                >
                  <Bell className="h-5 w-5" />
                  <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full">
                    {unreadMessageCount}
                  </span>
                </button>
              )}
              <div className="flex items-center gap-2 pl-3 border-l border-gray-200">
                <div className="w-9 h-9 rounded-full bg-[#FFB639] text-black flex items-center justify-center font-semibold text-sm shadow-sm">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
                <div className="hidden sm:block">
                  <p className="text-sm font-medium text-gray-900 leading-tight">{user?.name}</p>
                  <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50">
          <div className="container mx-auto px-6 py-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
